import styled from '@emotion/styled';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import useApiCall from '../hooks/useApiCall';
import { sendOTP, verifyOTP } from '../service/api.v2.service';
import { VerifyOTPRequest, VerifyOTPResponse } from '../types/api.v2.types';

const VERIFICATION_TIME = 180;
const DEV_NAMES = ['glen', 'paul', 'lina', 'tony'];
const BLOCK_NUMBERS = Array.from({ length: 20 }, (_, i) => String(i + 1).padStart(3, '0'));

const generateDevEmails = (name: string) =>
  BLOCK_NUMBERS.map((blockNum) => ({
    name,
    email: `${name}.blk${blockNum}@projectbuildup.io`,
  }));

export const OtpForm: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const [time, setTime] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<{ name: string; email: string }[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setSelectedIndex(-1);

    const matchingDevs = DEV_NAMES.filter((name) => name.toLowerCase() === value.toLowerCase());

    const allSuggestions = matchingDevs.flatMap(generateDevEmails);
    setSuggestions(allSuggestions);
    setShowSuggestions(allSuggestions.length > 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleSuggestionClick(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;
      }
    } else if (e.key === 'Enter' && !otpRequestLoading && validateEmail(email)) {
      e.preventDefault();
      executeOtpRequest(email);
    }
  };

  const handleSuggestionClick = (suggestion: { name: string; email: string }) => {
    setEmail(suggestion.email);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    emailInputRef.current?.focus();
  };

  const {
    execute: executeOtpRequest,
    error: otpRequestError,
    loading: otpRequestLoading,
  } = useApiCall<string, AxiosResponse>({
    apiCall: sendOTP,
    onSuccess: () => {
      setIsRequestSubmitted(true);
      setTime(VERIFICATION_TIME);
    },
  });

  const {
    execute: executeOtpVerification,
    error: otpVerificationError,
    loading: otpVerificationLoading,
  } = useApiCall<VerifyOTPRequest, VerifyOTPResponse>({
    apiCall: () => verifyOTP(email, otp),
    onSuccess: (data) => {
      window.location.href = `/${data.blockDto.subFolder}`;
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (time > 0) {
      timer = setTimeout(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    } else {
      setIsRequestSubmitted(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [time]);

  return (
    <FormContainer>
      <InputGroup>
        <EmailInputContainer>
          <StyledInput
            type="email"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            placeholder={t('otp.description')}
            ref={emailInputRef}
          />
          {showSuggestions && (
            <SuggestionsList>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem
                  key={suggestion.email}
                  onClick={() => handleSuggestionClick(suggestion)}
                  $isSelected={index === selectedIndex}
                >
                  <DevName>{suggestion.name}</DevName>
                  <BlockNumber>{suggestion.email.split('.')[1]}</BlockNumber>
                </SuggestionItem>
              ))}
            </SuggestionsList>
          )}
        </EmailInputContainer>
        <Button
          onClick={() => executeOtpRequest(email)}
          disabled={!validateEmail(email) || otpRequestLoading}
        >
          {isRequestSubmitted ? t('otp.resend') : t('otp.request')}
        </Button>
      </InputGroup>

      <InputGroup>
        <StyledInput
          type="text"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder={t('otp.enterCode')}
          maxLength={6}
          disabled={!isRequestSubmitted}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              !otpVerificationLoading &&
              otp.length === 6 &&
              /^\d{6}$/.test(otp)
            ) {
              executeOtpVerification({ email, pin: otp, language: i18n.language });
            }
          }}
        />
        <Timer>{formatMMSS(time)}</Timer>
        <Button
          onClick={() => executeOtpVerification({ email, pin: otp, language: i18n.language })}
          disabled={otpVerificationLoading || otp.length !== 6 || !/^\d{6}$/.test(otp)}
        >
          {t('otp.submit')}
        </Button>
      </InputGroup>
      {(otpRequestError || otpVerificationError) && (
        <ErrorMessage>{otpRequestError || otpVerificationError}</ErrorMessage>
      )}
    </FormContainer>
  );
};

const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const formatMMSS = (s: number) => {
  if (s === 0) {
    return '';
  }

  const minutes = Math.floor(s / 60);
  const seconds = Math.floor(s % 60);

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
};

const Timer = styled.div`
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  right: 212px;
  top: 50%;
  width: 88px;
  height: 36px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #495264;
  transform: translateY(-50%);
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  gap: 20px;
`;

const ErrorMessage = styled.p`
  color: #ef4444; // Tailwind의 text-red-500에 해당하는 색상
  font-size: 0.875rem; // Tailwind의 text-sm에 해당하는 크기
`;

const Button = styled.button`
  width: 180px;
  height: 120px;
  text-align: center;
  white-space: nowrap;
  border-radius: 12px;
  ${({ theme }) => theme.typography.SEMI_BOLD_24}
  background: #353c4d;
  color: #cdd1dd;

  &:disabled {
    cursor: not-allowed;
    background: #cdd1dd;
    color: #b2b7c4;
  }
`;

const EmailInputContainer = styled.div`
  position: relative;
  width: 500px;
  z-index: 1000;
`;

const StyledInput = styled.input`
  width: 500px;
  height: 120px;
  border-radius: 12px;
  border: solid 2px #cdd1dd;
  text-align: center;
  ${({ theme }) => theme.typography.REGULAR_24}
  color: #252b39;

  &::placeholder {
    ${({ theme }) => theme.typography.REGULAR_24}
    color: #b2b7c4;
    text-align: center;
  }

  &:disabled {
    background-color: #e6e8ef;
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid #cdd1dd;
  border-radius: 12px;
  list-style: none;
  padding: 8px 0;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.li<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background-color: ${({ $isSelected }) => ($isSelected ? '#f7f8fa' : 'transparent')};
  transition: background-color 0.2s;

  &:hover {
    background-color: #f7f8fa;
  }
`;

const DevName = styled.span`
  ${({ theme }) => theme.typography.REGULAR_16}
  font-weight: 600;
  color: #252b39;
  text-transform: capitalize;
`;

const BlockNumber = styled.span`
  ${({ theme }) => theme.typography.REGULAR_16}
  color: #495264;
`;
